import React, { useState } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { Row } from 'styled/alignment/Row';
import colors from 'theme/colors';
import Fieldset from 'components/_UI/Fieldset';
import debounce from 'helpers/debounce';
import OrdersTable, { REFUND_REQUESTS_TAB } from './OrdersTable';
import { List, CheckCircle, Clock, XCircle, RotateCcw, AlertTriangle, MessageSquareWarning } from 'lucide-react';
import { panelAccent } from 'theme/panelAccent';
import { REFUND_REQUESTS_TAB_PARAM, useRefundRequestsOptional } from 'context/refundRequests/RefundRequests';

export default function Orders() {
    const refundRequests = useRefundRequestsOptional();
    const pendingRefundRequests = refundRequests?.pendingCount || 0;

    const tabs = [
        { name: 'all', label: 'Todos', icon: List },
        // Quem não responde aos pedidos de estorno (equipe sem "Reembolsar") não vê a aba.
        refundRequests?.enabled && { name: REFUND_REQUESTS_TAB, label: 'Solicitações de estorno', icon: MessageSquareWarning, count: pendingRefundRequests },
        { name: 'approved', label: 'Aprovados', icon: CheckCircle },
        { name: 'pending', label: 'Pendentes', icon: Clock },
        { name: 'canceled', label: 'Expirados', icon: XCircle },
        { name: 'refunded', label: 'Estornos', icon: RotateCcw },
        { name: 'chargeback', label: 'Chargeback', icon: AlertTriangle },
    ].filter(Boolean);

    // A aba dos pedidos de estorno fica na URL: é para ela que o banner e o sino levam.
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedTab, setSelectedTab] = useState(() => (
        searchParams.has(REFUND_REQUESTS_TAB_PARAM) ? REFUND_REQUESTS_TAB : 'all'
    ));
    const [search, setSearch] = useState('');

    const handleSearch = debounce((term) => {
        setSearch(term);
    }, 500);

    function selectTab(name) {
        setSelectedTab(name);

        const next = new URLSearchParams(searchParams);

        if (name === REFUND_REQUESTS_TAB) {
            next.set(REFUND_REQUESTS_TAB_PARAM, '');
        } else {
            next.delete(REFUND_REQUESTS_TAB_PARAM);
        }

        setSearchParams(next, { replace: true });
    }

    return (
        <Wrapper>
            <Tabs role="tablist">
                {tabs.map(el => {
                    const IconComponent = el.icon;
                    return (
                        <TabItem
                            key={el.name}
                            role="tab"
                            aria-selected={el.name === selectedTab}
                            selected={el.name === selectedTab}
                            onClick={() => selectTab(el.name)}
                        >
                            <IconComponent size={15} />
                            <span>{el.label}</span>
                            {el.count > 0 && <TabCount aria-label={`${el.count} esperando resposta`}>{el.count}</TabCount>}
                        </TabItem>
                    );
                })}
            </Tabs>

            <SearchRow>
                <Fieldset
                    placeholder="Pesquisar por nome, e-mail, ID do pedido ou método de pagamento"
                    layout='squired'
                    badgeIcon='searchDark'
                    badgePosition='left'
                    textIndent='8px'
                    style={{ marginTop: 0 }}
                    onChangeValue={handleSearch}
                />
            </SearchRow>

            <OrdersTable status={selectedTab} search={search} />
        </Wrapper>
    );
}

const Wrapper = styled.div`
    margin-top: 20px;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    overflow: hidden;
`;

const Tabs = styled(Row)`
    padding: 0 20px;
    overflow: auto;
    gap: 0;
    border-bottom: 1px solid ${colors.neutralDark};

    &::-webkit-scrollbar {
        display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
`;

const TabCount = styled.span`
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 20px;
    background: ${colors.danger};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

const SearchRow = styled.div`
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const TabItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 14px 16px;
    border: none;
    border-bottom: 2px solid ${({ selected }) => selected ? panelAccent(colors.occasionalPurple) : 'transparent'};
    background: transparent;
    color: ${({ selected }) => selected ? colors.nightDark : colors.night};
    font-weight: ${({ selected }) => selected ? '600' : '400'};
    cursor: pointer;
    white-space: nowrap;
    font-size: 14px;
    font-family: inherit;
    transition: all 0.2s ease;
    margin-bottom: -1px;

    &:hover {
        color: ${colors.nightDark};
        border-bottom-color: ${({ selected }) => selected ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    }

    &:focus-visible {
        outline: 2px solid ${panelAccent(colors.occasionalPurple)};
        outline-offset: -2px;
    }
`;
